/**
 * Typed API client for the backend (today: email-intel-worker at
 * intel.lennar-hub.com; tomorrow: Uche's backend — same contracts, see
 * ../../API-CONTRACTS.md). Every function maps 1:1 to a worker route the
 * legacy form calls.
 *
 * Auth: requests pass the worker's gate via allowlisted Origin in production;
 * for local dev, set a service key with `setApiKey()` (legacy: ?devkey= →
 * localStorage) — appended as ?key= like the legacy form does.
 */
import { scrubPackImages } from "./echLiveImg";
import type {
  SfmcCommunitySearchResponse,
  CommunityPackResponse,
  SubmitBody,
  SubmitResponse,
} from "../types/api";
import { orderHomesites } from "./echOrder";
import { API_BASE, API_CLIENT_ID, API_KEY, API_MODE } from "./authConfig";
import { getAccessToken, getCurrentUser } from "./auth";

/* Same-origin by default: the app calls /api/* and the frontend's own server
   forwards to emailbuilder-be (CRA dev proxy locally, nginx proxy_pass in EKS).
   setApiBase() still lets a build target the legacy worker for golden-diffing. */
let BASE = "/api";
let KEY = "";

/** The worker origin — legacy; only the file:// viewer parity below still points at it. */
export const WORKER_BASE = "https://intel.lennar-hub.com";

/*
 * DEV HOST → WORKER override REMOVED (2026-08-25).
 *
 * emailbuilder-be reached submit parity (141-field mirror, feeds routes,
 * /field-options — emailbuilder-be#8), which was this override's own stated
 * removal condition. Non-prod hosts now resolve like everywhere else: the
 * gateway when API_BASE is set, the same-origin `/api` proxy otherwise.
 * The `?api=` query override went with it — routing is Portal-config only.
 */
/** non-prod frontends: emailbuilder-{dev,qa,uat}.lennar.com — prod has no -env suffix */
export const NONPROD_HOST = /^emailbuilder-(dev|qa|uat)\.lennar\.com$/i;

/* file:// viewer parity (build/index.local.html, any double-clicked build): there is no
   /api proxy on disk, but the monolith STILL pulls live data there — its CONFIG points
   straight at the worker (index.html L4069) and its devkey shim (L4071–4086) reads
   ?devkey= → localStorage("lsr_devkey"). Mirror both, so live reads (/initiatives —
   which is why the monolith's fresh ATL load picks "3147 - Grand Opening for Estates
   at Yellow Creek" while a proxy-less build fell back to the snapshot's 3104 —
   /refdata, /templates.json, /template-html) match the monolith side-by-side.
   file:// pages share one localStorage origin, so a devkey unlocked for the monolith
   unlocks the viewer too. */
try {
  if (typeof window !== "undefined" && window.location.protocol === "file:") {
    BASE = "https://intel.lennar-hub.com";
    let qk = "";
    try { qk = new URLSearchParams(window.location.search).get("devkey") || ""; } catch { /* no search */ }
    if (qk) { try { localStorage.setItem("lsr_devkey", qk); } catch { /* private mode */ } }
    try { KEY = qk || localStorage.getItem("lsr_devkey") || ""; } catch { /* private mode */ }
  }
} catch { /* non-browser (tests) */ }

/**
 * GATEWAY MODE (Email Creative Hub API).
 *
 * When a gateway origin is configured (authConfig.API_BASE, per-env runtime
 * config), it WINS over `/api` and the worker/file:// overrides above: every
 * route lives under `/emailcreativehub` on the gateway, and each request carries
 * the Entra access token (`Authorization: Bearer …`) plus `X-Client-Id` — see
 * `apiFetch`. Unset (local `npm start`, prod-today) → behaviour is unchanged.
 */
export const ECH_PREFIX = "/emailcreativehub";

/**
 * PROXY MODE (break-glass, non-prod only).
 *
 * `API_MODE=proxy` (Portal runtime config) sends every call to the same-origin
 * `/api` proxy (nginx → emailbuilder-be) INSTEAD of the gateway, even while
 * API_BASE stays set — one env var to flip when the gateway is down, one to
 * flip back. Same-origin means no CORS and no Entra bearer on the API calls,
 * which is exactly why production REFUSES it: prod (`emailbuilder.lennar.com`,
 * no -env suffix) must only ever reach the backend through the authenticated
 * gateway. Refusal is by host pattern, so no config mistake can bypass it.
 */
export const PROXY_MODE: boolean = (() => {
  if (API_MODE !== "proxy") return false;
  try {
    if (typeof window === "undefined") return true; /* tests / non-browser */
    const h = window.location.hostname;
    if (NONPROD_HOST.test(h) || h === "localhost" || h === "127.0.0.1") return true;
    console.error("[api] API_MODE=proxy is for non-prod hosts only — refused here; staying on the gateway.");
    return false;
  } catch {
    return false;
  }
})();

/** True once the app is pointed at the gateway rather than the /api proxy. */
export const GATEWAY_MODE = Boolean(API_BASE) && !PROXY_MODE;
if (GATEWAY_MODE) BASE = API_BASE + ECH_PREFIX;
if (PROXY_MODE) BASE = "/api";

export function setApiBase(b: string) { BASE = b.replace(/\/$/, ""); }
export function setApiKey(k: string) { KEY = k; }

/**
 * Attach gateway auth to a request. In gateway mode every call gets the
 * `X-Client-Id` header, the `X-API-Key` usage-plan key when one is configured,
 * and, once the user is signed in and an API scope is
 * exposed, the Entra bearer (getAccessToken returns null until then — the call
 * still goes out and the gateway answers 401, which is the expected "scope not
 * yet exposed" signal). Outside gateway mode this is a no-op passthrough, so the
 * /api proxy path is byte-for-byte what it was.
 */
async function authInit(init?: RequestInit): Promise<RequestInit> {
  if (!GATEWAY_MODE) return init ?? {};
  const headers = new Headers(init?.headers);
  if (API_CLIENT_ID) headers.set("X-Client-Id", API_CLIENT_ID);
  if (API_KEY) headers.set("X-API-Key", API_KEY);
  const token = await getAccessToken();
  if (token) headers.set("Authorization", "Bearer " + token);
  return { ...init, headers };
}

/** fetch() with gateway auth applied. Use for every backend call in this module. */
async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  return fetch(url, await authInit(init));
}

/**
 * Template thumbnail URL (backend /thumb/:id, via the same base).
 *
 * ⚠️ GATEWAY CAVEAT: this URL is consumed directly as an `<img src>`
 * (TemplateGallery), so the browser fetches it with NO `Authorization` /
 * `X-Client-Id` header — those can't be attached to an image request. It works
 * only if the gateway serves `/thumb/{id}` (and other raw-URL GETs) WITHOUT
 * requiring the Entra bearer. If the gateway enforces auth on it, thumbnails
 * 401 and we must switch to `apiFetch` → object URL. To verify against the live
 * gateway. Same caveat applies to any other raw-URL GET used in `src`/`href`.
 */
export const thumbUrl = (id: string) => `${BASE}/thumb/${id}`;

/** submit feedback (POST /feedback → Airtable). category ∈ User Experience | Technical Issue | Wish List */
export async function sendFeedback(fb: { message: string; category?: string; division?: string; by?: string; build?: string; images?: { name: string; data: string }[]; context?: string }): Promise<{ ok?: boolean; error?: string }> {
  const r = await apiFetch(u("/feedback"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(fb) });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean; error?: string };
}

/** division favorites (saved-HTML ledger). */
export interface Favorite { id: string; name: string; div: string; versions: number; key: string; source: string; updated: string; }
export const getFavorites = (div?: string) =>
  getJSON<{ ok: boolean; count: number; items: Favorite[] }>("/fav-list", { div });
export const favHtmlUrl = (key: string) => `${BASE}/fav-html?key=${encodeURIComponent(key)}`;

/**
 * GET /fav-html → the favorite's saved HTML, "" when missing. Prefer this over
 * `fetch(favHtmlUrl(...))` at call sites: it goes through `apiFetch`, so the
 * Entra bearer + `X-Client-Id` are attached in gateway mode.
 */
export async function getFavoriteHtml(key: string): Promise<string> {
  const r = await apiFetch(favHtmlUrl(key));
  return r.ok ? r.text() : "";
}

function u(path: string, params?: Record<string, string | number | undefined>): string {
  const q = new URLSearchParams();
  if (params) for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== "") q.set(k, String(v));
  if (KEY) q.set("key", KEY);
  const qs = q.toString();
  return BASE + path + (qs ? "?" + qs : "");
}

async function getJSON<T>(path: string, params?: Record<string, string | number | undefined>): Promise<T> {
  const r = await apiFetch(u(path, params), { cache: "no-store" });
  if (!r.ok) throw new Error(path + " → " + r.status);
  return (await r.json()) as T;
}

/* ── SFMC data (Sitecore-backed DEs) ─────────────────────────────── */

/** governed Content Builder blocks + Lauren's blank shell (monolith __grSync fetch) */
/* rebuild=1 forces the worker past its R2 cache — the "I just edited the block" path */
export const getSfmcBlocks = (opts?: { rebuild?: boolean; fresh?: boolean }) =>
  getJSON<import("./sfmcBlocks").SfmcBlocksResponse>(
    "/sfmc-blocks",
    opts?.rebuild ? { op: "list", deep: 1, rebuild: 1 }
      /* `fresh=1` skips the backend's 60s list cache without the R2 rebuild — the
         watcher's poll (sfmcSource.sfmcBlocksWatch) uses it */
      : opts?.fresh ? { op: "list", deep: 1, fresh: 1 }
        : { op: "list", deep: 1 },
  );

export const searchCommunities = (q: string) =>
  getJSON<SfmcCommunitySearchResponse>("/sfmc-communities", { q });

export const getCommunityPack = (community: string) =>
  getJSON<CommunityPackResponse>("/sfmc-content", { community }).then((C) => {
    /* homes enter the app ALREADY in serving order — move-in ready first, then
       under construction, photo-backed leading within each (echOrder, Jack's own
       Spec Sheet doctrine). One point, so the fill, the wizard picker, Add by ID
       and the counts can never disagree about which ten homes lead. */
    if (C && Array.isArray(C.homesites)) C.homesites = orderHomesites(C.homesites);
    /* …and every photo a card can pick has been asked whether it loads. A dead
       hero (cdn.lennar.com 404 — Litmus 2026-09-09, Ashbury) is blanked here so the
       fill's own precedence falls to the next one; see echLiveImg. */
    return scrubPackImages(C);
  });

/**
 * Division inventory feeds as RAW JSON — `/homesites.json` (available homes) and
 * `/plans.json` (floorplans, every status: `all=1` — echDirect keeps ACTIVE + COMING_SOON
 * and drops SOLD_OUT; see COMING-SOON FLOORPLANS there), whole division. Routed through the api-layer
 * on purpose: it inherits `BASE` (worker / `/api` / gateway) and gateway auth like
 * every other call, so `echDirect` no longer fetches `WORKER_BASE` directly. The
 * caller owns parsing the `items` rows into packs (see echDirect.fetchDirect).
 */
export const getInventoryFeed = (level: "homes" | "plans", div: string) =>
  getJSON<{ items?: unknown[] }>(level === "homes" ? "/homesites.json" : "/plans.json", level === "homes"
    ? { div: String(div || "").trim().toUpperCase() }
    : { div: String(div || "").trim().toUpperCase(), all: "1" });

export interface DivisionContent { code: string; name: string; phone: string; hero: string; }
export const getDivisionContent = () =>
  getJSON<{ ok: boolean; items: DivisionContent[] }>("/sfmc-division-content");

/**
 * The division's own Contact Us layoutblock, slots assembled (audit row 24).
 *
 * `imgCount` is not decoration: a `layoutblock` read through `views.html.content`
 * comes back as a few hundred bytes of shell with no ISC photograph in it, and
 * swapping THAT in would blank a working block. The worker reports how many
 * images the assembled markup carries, and the caller refuses anything with none
 * — the monolith's own guard (`d.ok && d.html && d.imgCount`, index.html L11687).
 * Live-verified 2026-08-19: AZH → 5,614 bytes / imgCount 1, ATL → 6,011 / 1.
 */
export const getIscBlock = (div: string) =>
  getJSON<{ ok: boolean; html?: string; imgCount?: number }>("/sfmc-isc", { div });

/**
 * ONE ROW FROM A DATA EXTENSION, BY EXTERNAL KEY + PRIMARY KEY (2026-09-02).
 * The read half of a `dataSource` block. Columns come back LOWERCASED — the API's
 * casing, not the DE's — and every cell is "" rather than absent, so an empty column
 * and a missing column cannot be confused. `found:false` is a real answer (a division
 * with no row), not an error.
 */
export interface DeRowResponse {
  ok: boolean; key: string; pk: string; found: boolean;
  columns: string[]; row: Record<string, string> | null; rows: number; cached: boolean;
}
export const getDeRow = (key: string, pk: string, pkcol?: string) =>
  getJSON<DeRowResponse>("/sfmc-de-row", pkcol ? { key, pk, pkcol } : { key, pk });

export interface SocialLinkRow { code: string; fb?: string; x?: string; ig?: string; pin?: string; yt?: string; li?: string; }
export const getSocialLinks = () =>
  getJSON<{ ok: boolean; divisions: SocialLinkRow[]; markets: SocialLinkRow[] }>("/sfmc-social-links");

/* ── Templates & reference data (Airtable/R2-backed) ─────────────── */

export interface GalleryTemplate { id: string; name: string; thumb?: string; stage?: string; category?: string; mainMessage?: string; status?: string; hasHtml?: boolean; }
export const getTemplates = () =>
  getJSON<{ templates?: GalleryTemplate[]; items?: GalleryTemplate[] }>("/templates.json");

export async function getTemplateHtml(id: string): Promise<string> {
  const r = await apiFetch(u("/template-html", { id }), { cache: "force-cache" });
  if (!r.ok) throw new Error("/template-html → " + r.status);
  return r.text();
}

/** Legal Reviews › Disclaimer Options, fully paged by the backend (GET /disclaimer/options,
 *  emailbuilder-be). Read by disclaimerLegal.ts for legal's wording (2026-10-28). */
export const getDisclaimerOptions = () =>
  getJSON<{ records?: { id: string; fields?: Record<string, unknown> }[] }>("/disclaimer/options");

export const getInitiatives = (div?: string) =>
  getJSON<{ div: string | null; initiatives?: string[] }>("/initiatives", { div });
/**
 * `/refdata` — division-scoped picklists, PLUS two payloads the app used to fetch
 * and throw away (audit "Needs a backend route" §1; the fix is client-side only):
 *
 *  · `ids` — the record-id registry (schema 2), community/masterplan NAME → Airtable
 *    record id.
 *  · `addr` — (schema 3) Address / City / State / Zip **and `pageUrl`** keyed by that
 *    record id, for communities AND masterplans separately, so an MPC maps to its OWN
 *    row rather than borrowing a sub-collection's address. Airtable is the source
 *    here, not SFMC's content pack: the pack has these fields blank on plenty of
 *    communities and has no row at all for many masterplans.
 *
 * `pageUrl` matters because `community.pageurl` is the default destination for every
 * CTA and image link across 7 ECH blocks — without it those fields have no source.
 */
export interface RefdataAddr {
  address?: string; city?: string; state?: string; zip?: string;
  pageUrl?: string; code?: string; market?: string; amenities?: string;
  /**
   * The community's own Area of Interest and Submarket — `refdata` schema **8**.
   * Already declared on `echDyn`'s `AddrRow` (the same payload, read through a second
   * type); adding them here is what lets the WIZARD read them too, which is how the
   * community step groups by area (2026-08-22). `aoi` CAN BE A COMMA LIST
   * ("Harrah, East OKC") — split it, never compare whole. Optional: 2,153 of 3,095
   * rows carry one, and an older cached payload has neither.
   */
  aoi?: string;
  submarket?: string;
  /**
   * The community's hero photo — `refdata` schema **7**, 2026-08-20.
   *
   * Airtable has no hero, so the wizard's "Choose communities" grid drew a blank navy
   * gradient on every card it hadn't already loaded a pack for: browsing showed no
   * photos, searching did, because `/sfmc-communities` returns `hero_image_url` and the
   * division list didn't. The worker now joins it from SFMC's Communities DE by
   * community code, so the browse grid has thumbnails with no extra client requests.
   *
   * Optional on purpose — the join is non-fatal in the worker, and an older cached
   * `refdata.json` (schema ≤ 6) simply has no `hero`. Treat absent as "no photo", never
   * as an error.
   */
  hero?: string;
}
export interface RefdataResponse {
  div?: string | null;
  divisionId?: string;
  markets?: string[];
  communities?: string[];
  masterplans?: string[];
  areas?: string[];
  initiativeIds?: Record<string, string>;
  ids?: {
    markets?: Record<string, string>;
    communities?: Record<string, string>;
    masterplans?: Record<string, string>;
    areas?: Record<string, string>;
  };
  addr?: {
    communities?: Record<string, RefdataAddr>;
    masterplans?: Record<string, RefdataAddr>;
  };
  /** the division's raw Creative Studio › Divisions license text (backend 2026-10-23,
   *  emailbuilder-be #70); absent on an older backend. Read by disclaimerLicense.ts. */
  license?: { seller?: string; construction?: string; states?: string };
}
export const getRefdata = (div?: string) => getJSON<RefdataResponse>("/refdata", { div });

/**
 * Live select-field options, read off the PRODUCTION Projects table's schema (added 2026-08-17).
 *
 * The picklists — Email Template Request Type, Sender Type, Email Audience Type, Offer Type,
 * Target Audience/Buyer Type, RUSH — are currently hardcoded constants in `../data/options`
 * (and were the same in the CloudPage form), so an option the ops team adds in Airtable never
 * appears until someone edits and redeploys. This endpoint closes that: the worker reads the
 * select choices from the base schema (Meta API, schema-read only, no records) keyed by
 * Airtable FIELD NAME, so adding a new picklist needs no worker or client change.
 *
 *   (await getFieldOptions()).fields["Sender Type"]
 *     → ["Market", "MarketSpanish", "Masterplan", …]
 *
 * Live-verified 2026-08-17: 118 select fields; counts match the ops field reference exactly
 * (13 request types / 8 sender / 11 audience / 18 offer / 14 buyer / 3 RUSH). `known`
 * distinguishes "no options" from "no such field" — i.e. an empty list from a typo. On
 * failure, keep the hardcoded list rather than rendering an empty select.
 */
export interface FieldOptionsResponse {
  ok: boolean;
  fields?: Record<string, string[]>;
  count?: number;
  field?: string;
  options?: string[];
  known?: boolean;
  cachedAt?: number | null;
  error?: string;
}
export const getFieldOptions = (field?: string) => getJSON<FieldOptionsResponse>("/field-options", { field });
/**
 * The amenity icon library, read off the SFMC folder Layne keeps it in
 * (Content Builder > Test - Jack Blocks > Amenity Icons).
 *
 * `missingPublishedUrl` is the number of assets in that folder with no hosted
 * URL — reported rather than dropped, because an icon the picker cannot offer is
 * a folder problem someone has to see. Rows without a url are filtered client-side
 * by setAmenFolderLib.
 */
export interface AmenityIconRow { label: string; name: string; id: number; url: string; group: string; groupSource: string }
export const getAmenityIcons = () =>
  getJSON<{ ok: boolean; count: number; icons: AmenityIconRow[]; missingPublishedUrl: number; folder: string;
    groupOrder: string[]; subfolders: string[]; taxonomy: "folder" | "fallbackMap" }>("/amenity-icons");

/** `app` → only jobs submitted from the app, each with its SFMC `assetId` (Resubmit, 2026-10-13) */
export const getJobs = (div: string, opts?: { app?: boolean }) =>
  getJSON<{ ok: boolean; jobs: Record<string, unknown>[] }>("/jobs", opts && opts.app ? { div, app: "1" } : { div });
/** Projects (Mirror) rows — the Review Board's session-independent source (monolith admMirrorLoad) */
export const getMirrorJobs = () => getJSON<{ ok?: boolean; jobs?: Record<string, unknown>[] }>("/mirror-jobs");

/** POST /mirror-status — the Review Board's approve/route decision (authed in gateway mode). Throws on non-2xx. */
export async function mirrorStatus(body: { recordId?: string; assetId?: string | number; jobStatus: string; decision?: string; notes?: string }): Promise<{ ok?: boolean; error?: string }> {
  const r = await apiFetch(u("/mirror-status"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error("mirror-status → " + r.status);
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean; error?: string };
}

/** `rec` → the worker pulls the Airtable proof link for pre-archive sends (monolith dupApply, 2026-07-23) */
export const getSentEmail = (p: { subject?: string; date?: string; div?: string; name?: string; rec?: string }) =>
  getJSON<{ ok: boolean; html?: string; via?: string; jobId?: string }>("/sent-email", p);

/** RESUBMIT (2026-10-13): the email an app job was submitted with — the backend's
    /eblast-preview, raw=1 so the AMPscript and the ECH-MANIFEST block record survive.
    "" when there is none, so the caller can fall back to the subject match. */
export async function getSubmittedHtml(assetId: string): Promise<string> {
  if (!/^[0-9]{1,15}$/.test(String(assetId || ""))) return "";
  /* latest=1 (2026-10-29): the newest version — every in-app revision, or Content Builder's
     copy if someone edited it there afterwards (the backend decides) */
  const r = await apiFetch(u("/eblast-preview", { id: assetId, raw: "1", latest: "1" }), { cache: "no-store" });
  if (!r.ok) return "";
  return r.text();
}

/** save a delivered email as a division favorite (monolith __favFromJob → POST /fav-save) */
export async function favSave(body: { div: string; name: string; html: string; source?: string; baseTemplate?: string }): Promise<{ ok?: boolean; id?: string; version?: number }> {
  const r = await apiFetch(u("/fav-save"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean; id?: string; version?: number };
}
/** remove a division favorite (monolith star-unsave → POST /fav-delete) */
export async function favDelete(id: string): Promise<{ ok?: boolean }> {
  const r = await apiFetch(u("/fav-delete"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean };
}

/* ── NAMED DRAFTS (2026-09-18, dev/QA) — see services/namedDrafts.ts ──────────
   Same ledger idea as favorites, keyed by division + name so a re-save overwrites
   in place: /draft-save, /draft-list, /draft-doc, /draft-html, /draft-delete.
   The hosted preview (`previewUrl`) is served by the backend's public
   /draft-preview?id= — the same shape as the request row's /eblast-preview. */
export async function draftSaveRemote(body: { div: string; name: string; who: string; html: string; doc: string; tpl?: string }): Promise<{ ok?: boolean; id?: string; previewUrl?: string; savedAt?: string; error?: string }> {
  const r = await apiFetch(u("/draft-save"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  return (await r.json().catch(() => ({ ok: false, error: "HTTP " + r.status }))) as { ok?: boolean; id?: string; previewUrl?: string; savedAt?: string; error?: string };
}
export const getNamedDrafts = (div?: string) =>
  getJSON<{ ok: boolean; count: number; items: import("./namedDrafts").NamedDraft[] }>("/draft-list", { div });
/** the draft's stored document (form + audience + blocks), "" when missing */
export async function getNamedDraftDoc(id: string): Promise<string> {
  const r = await apiFetch(u("/draft-doc", { id }), { cache: "no-store" });
  return r.ok ? r.text() : "";
}
/** the draft's rendered HTML — for the picker's minis */
export async function getNamedDraftHtml(id: string): Promise<string> {
  const r = await apiFetch(u("/draft-html", { id }), { cache: "no-store" });
  return r.ok ? r.text() : "";
}
export async function draftDeleteRemote(id: string): Promise<{ ok?: boolean }> {
  const r = await apiFetch(u("/draft-delete"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean };
}
/** stamp a draft as submitted (recordId) — the sent sweep clears it later */
export async function draftMarkSubmitted(id: string, recordId?: string): Promise<{ ok?: boolean }> {
  const r = await apiFetch(u("/draft-submitted"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id, recordId: recordId || "" }) });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean };
}

/* ── Intake ──────────────────────────────────────────────────────── */

/**
 * POST /sfmc-image — host a browser-baked image (hero slideshow GIF, overlay hero) as a
 * Content Builder IMAGE asset and get SFMC's published URL back (2026-09-16). Throws on
 * anything but ok:true so the caller decides whether the submit may go on without it.
 */
export async function hostImage(dataUrl: string, name: string): Promise<{ url: string; id: number }> {
  const r = await apiFetch(u("/sfmc-image"), { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name, dataUrl }) });
  const j = (await r.json().catch(() => ({}))) as { ok?: boolean; url?: string; id?: number; error?: string };
  if (!r.ok || !j.ok || !j.url) throw new Error(j.error || ("/sfmc-image → " + r.status));
  return { url: j.url, id: j.id || 0 };
}

/**
 * A rejection from the gateway itself, before the backend ran. Carries the HTTP status
 * and the gateway's own message so the Done screen and the console can name it.
 */
export class GatewayError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "GatewayError";
    this.status = status;
  }
}

/** the gateway's own rejection bodies are `{"message":…}` (401 Unauthorized, 403 explicit
 *  deny, 413 Request Too Long, 429 Too Many Requests) or `{"Message":…}`; the backend's are
 *  `{"ok":false,"error":…}`. Read whichever is there. */
function gatewayMessage(text: string, status: number): string {
  try {
    const j = JSON.parse(text) as { message?: unknown; Message?: unknown; error?: unknown };
    const m = j.message ?? j.Message ?? j.error;
    if (typeof m === "string" && m.trim()) return m.trim();
    if (m !== undefined && m !== null) return JSON.stringify(m).slice(0, 200);
  } catch { /* not JSON */ }
  return "HTTP " + status;
}

/**
 * POST /sfmc-submit.
 *
 * ── THE STATUS LINE IS PART OF THE ANSWER (2026-09-22, Alexis Rashid, ATL) ──────────
 * This used to `return r.json()` with no look at `r.ok`. The gateway answers a request
 * it refuses with JSON of its own — `401 {"message":"Unauthorized"}`, `403 {"message":
 * "User is not authorized…"}`, 413, 429 — none of which carries `ok` or `error`, so the
 * caller's `r.ok !== false && !r.error` read every one of them as a filed request. Alexis
 * submitted several requests on prod, saw "Request submitted" each time, and nothing had
 * reached the backend: no Airtable row, no Content Builder asset, and her draft was
 * discarded on the strength of the green screen. Measured on the dev gateway the same
 * day: no bearer → 401, bad bearer → 403, both parsed clean and both would have shown
 * the success screen.
 * Now: a non-2xx throws a GatewayError naming the status and the gateway's message, and
 * a 2xx whose body does not carry the backend's `ok` boolean throws too (the backend
 * ALWAYS sets it; a body without it did not come from the backend). The caller's catch
 * routes a throw to the error screen and keeps the draft.
 */
export async function submitRequest(body: SubmitBody): Promise<SubmitResponse> {
  const r = await apiFetch(u("/sfmc-submit"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) throw new GatewayError(r.status, gatewayMessage(text, r.status));
  let j: unknown;
  try { j = JSON.parse(text); } catch { throw new GatewayError(r.status, "the reply was not JSON"); }
  if (!j || typeof (j as { ok?: unknown }).ok !== "boolean") {
    throw new GatewayError(r.status, "the reply did not come from the backend (" + gatewayMessage(text, r.status) + ")");
  }
  return j as SubmitResponse;
}

/**
 * One cheap authenticated GET, run once after sign-in, so an account the gateway refuses
 * is told at the front door instead of at submit (2026-09-22). `denied` is a 401/403 from
 * the gateway; `ok` is anything the gateway let through (the backend's own answer does
 * not matter here); `unknown` is a network failure, which is not a verdict on the account.
 * Outside gateway mode there is nothing to probe.
 */
export async function probeGatewayAccess(): Promise<{ verdict: "ok" | "denied" | "unknown"; status: number; message?: string }> {
  if (!GATEWAY_MODE) return { verdict: "ok", status: 0 };
  try {
    const r = await apiFetch(u("/fav-list", { div: "PROBE" }), { cache: "no-store" });
    if (r.status === 401 || r.status === 403) {
      return { verdict: "denied", status: r.status, message: gatewayMessage(await r.text().catch(() => ""), r.status) };
    }
    return { verdict: "ok", status: r.status };
  } catch {
    return { verdict: "unknown", status: 0 };
  }
}

/**
 * POST /mirror-revise — files an email build against an existing intake row
 * (create-mode when the row has no asset yet: makes the TEST_ Content Builder
 * asset, stores the hosted preview, stamps the row).
 *
 * WHY the form calls this after submitting: /sfmc-submit stores small
 * custom-html builds but silently refuses ~100KB template-mode docs
 * (`customHtmlStored:false`, no error) — so a template-edit request would
 * file with NO creative attached and Creative would never see the build.
 * This route handles 100K+ docs (it's the same path the review console's
 * "Save revision" uses), so the form re-files the HTML here when submit
 * didn't take it. Measured 2026-08-09: 1KB via submit → stored; 96KB via
 * submit → refused; the same 96KB via this route → stored.
 */
export async function mirrorRevise(body: { recordId?: string; assetId?: string; html: string; project?: string; div?: string }): Promise<{ ok?: boolean; assetId?: number | string; previewUrl?: string; revision?: number; error?: string }> {
  const r = await apiFetch(u("/mirror-revise"), {
    method: "POST",
    /* the signed-in user, like the proofing calls: the backend checks who is overwriting an
       existing email (2026-10-14) and reads them from this header (Farhan, 2026-10-26) */
    headers: { "Content-Type": "application/json", ...proofIdentityHeaders() },
    body: JSON.stringify(body),
  });
  return (await r.json().catch(() => ({ ok: false }))) as { ok?: boolean; assetId?: number | string; previewUrl?: string; revision?: number; error?: string };
}

/* ── Bynder (DAM) ────────────────────────────────────────────────── */

/**
 * The bytes of a Bynder-hosted image, fetched through the backend's `/bynder-img` proxy
 * (same origin, gateway auth) — the browser cannot read lennar.bynder.com bytes directly
 * (no CORS). Used once per GIF pick to count frames (services/bynderGif.ts). Throws on a
 * non-2xx so the caller can say "could not check" rather than "still".
 */
export async function bynderImageBytes(src: string): Promise<Uint8Array> {
  /* `Accept: image/*` — the API Gateway maps binary responses by the request's Accept
     (Farhan, 2026-09-18, registering /emailcreativehub/bynder-img); the proxy path ignores it */
  const r = await apiFetch(u("/bynder-img", { src }), { cache: "no-store", headers: { Accept: "image/*" } });
  if (!r.ok) throw new Error("/bynder-img → " + r.status);
  return new Uint8Array(await r.arrayBuffer());
}

export interface BynderAsset {
  id: string;
  name: string;
  ext?: string;
  w?: number;
  h?: number;
  /** small preview */
  thumb: string;
  /** a usable image URL — the transform base at a default width (worker, 2026-08-22) */
  url: string;
  /**
   * Bynder's Dynamic Asset Transformation BASE (`.../transform/<id>/<name>`).
   * Append `?io=transform:fill,width:W,height:H` for an exact size. Present since
   * the worker stopped handing back `thumbnails.webimage`, which was a PREVIEW no
   * email client could load.
   */
  transform?: string;
  thumbnails?: Record<string, string>;
}

/**
 * Search Lennar's Bynder DAM. `filters` uses READABLE metaproperty names,
 * AND-combined: "Division_Code:ATL;Plan_Asset_Type:Interiors" — the worker
 * resolves names → Bynder option ids (cached). `q` is free-text keyword.
 */
export const bynderSearch = (p: { q?: string; filters?: string; limit?: number; page?: number }) =>
  getJSON<{ ok: boolean; count: number; page: number; items: BynderAsset[] }>("/bynder-search", {
    q: p.q,
    filters: p.filters,
    limit: p.limit ?? 30,
    page: p.page ?? 1,
  });


/* ── ECH PROOFING (Milestone 2, 2026-10-06) ─────────────────────────────────────
   Comments, lock and rounds on a submitted email. Contract: emailbuilder-be
   docs/proofing-api.md. The SERVER decides who may do what; every write answers
   with the fresh state, so the page never re-fetches after acting.

   Identity: in production the gateway forwards the verified user. The `x-ech-user`
   headers below are read by the backend ONLY when dev trust is switched on there
   (PROOF_DEV_TRUST_CLIENT) — they are a dev convenience, never the gate. */
export type ProofRole = "reviewer" | "editor" | "admin";
export interface ProofActor { email: string; name: string; role: ProofRole }
export interface ProofAnchor { block?: string; x: number; y: number }
export interface ProofReply { id: string; author: ProofActor; text: string; createdAt: string; editedAt?: string }
export interface ProofComment {
  id: string; n: number; round: number; cycle?: number; version: number | null; anchor: ProofAnchor; tag: string;
  author: ProofActor; text: string; createdAt: string; editedAt?: string;
  state: "open" | "resolved"; resolvedBy?: ProofActor; resolvedAt?: string; replies: ProofReply[];
}
/** a spot Digital Ops points the division at, saved with a version; no `text` = the whole block (10-26) */
export interface ProofHighlight { block: string; text?: string; note?: string }
export interface ProofVersion { n: number; by: ProofActor; t: string; note: string; highlights?: ProofHighlight[] }
/** one opened round: its (correctable) number and the version it showed the division */
export interface ProofRound { cycle: number; round: number; openedAt: string; base: number | null }
export interface ProofState {
  locked: boolean; lockKind: "start" | "editor" | "reviewer"; lockedBy: ProofActor | null; lockedAt: string | null;
  round: number; roundOpenedAt: string | null;
  cycle?: number; rounds?: ProofRound[];
  approved: boolean; approvedBy: ProofActor | null; approvedAt: string | null;
  comments: ProofComment[]; versions: ProofVersion[];
  /** the division's latest decision on a round (mockup: Request changes / Approve) */
  decision?: { round: number; value: "changes" | "approved"; by: ProofActor; t: string } | null;
}
export interface ProofCan { comment: boolean; lock: boolean; unlock: boolean; setround?: boolean; submit: boolean; save: boolean; approve: boolean; resolve: boolean; editContent: boolean }
export interface ProofView { ok: boolean; error?: string; status?: number; me?: ProofActor; state?: ProofState; can?: ProofCan; previewPath?: string }

/** dev-only identity hint (see the note above); the signed-in account when there is one */
export function proofIdentityHeaders(): Record<string, string> {
  const h: Record<string, string> = {};
  let email = "", name = "";
  try { const cu = getCurrentUser(); if (cu && cu.email) { email = cu.email; name = cu.name; } } catch { /* auth off */ }
  if (!email && process.env.NODE_ENV !== "production") { try { email = window.localStorage.getItem("ech.proofAs") || ""; } catch { /* storage blocked */ } }
  /* local `npm start` only: ?as=<email> picks who you are, so one laptop can click through
     as a reviewer and as an editor. Compiled out of production builds. */
  if (!email && process.env.NODE_ENV !== "production") { try { email = new URLSearchParams(window.location.search).get("as") || ""; } catch { /* no window */ } }
  if (email) h["x-ech-user"] = email;
  if (name) h["x-ech-user-name"] = name;
  return h;
}

async function proofJSON(r: Response): Promise<ProofView> {
  let j: ProofView;
  try { j = (await r.json()) as ProofView; } catch { j = { ok: false, error: "The server didn't answer. Try again." }; }
  j.status = r.status;
  if (!r.ok && j.ok !== false) j.ok = false;
  return j;
}

export async function getProof(id: string): Promise<ProofView> {
  const r = await apiFetch(u("/proof", { id }), { cache: "no-store", headers: proofIdentityHeaders() });
  return proofJSON(r);
}

export type ProofOp = "comment" | "reply" | "edit" | "delete" | "resolve" | "lock" | "unlock" | "submit" | "save" | "approve";
export async function proofPost(op: ProofOp, body: Record<string, unknown>): Promise<ProofView> {
  const r = await apiFetch(u("/proof-" + op), {
    method: "POST",
    headers: { "Content-Type": "application/json", ...proofIdentityHeaders() },
    body: JSON.stringify(body),
  });
  return proofJSON(r);
}

/** a saved version's HTML ("" when missing) — through apiFetch so gateway auth rides along */
export async function getProofVersionHtml(id: string, v: number): Promise<string> {
  const r = await apiFetch(u("/proof-version", { id, v, part: "html" }), { cache: "no-store", headers: proofIdentityHeaders() });
  return r.ok ? r.text() : "";
}

/** the job's send date, subject and preheader from its Airtable row — read-only (M2 Discovery, 2026-10-12) */
export interface ProofJob { record: string; name: string; sendAt: string; subject: string; preheader: string; source: "airtable" | "local" }
export async function getProofJob(id: string): Promise<ProofJob | null> {
  try {
    const r = await apiFetch(u("/proof-job", { id }), { cache: "no-store", headers: proofIdentityHeaders() });
    if (!r.ok) return null;
    const j = (await r.json()) as { ok?: boolean; job?: ProofJob | null };
    return j && j.ok && j.job ? j.job : null;
  } catch { return null; }
}

/** the submitted email as the hosted preview has it (jobs filed before proofing have no versions) */
export async function getEblastPreviewHtml(id: string): Promise<string> {
  const r = await apiFetch(u("/eblast-preview", { id }), { cache: "no-store" });
  return r.ok ? r.text() : "";
}

/** a saved version's editable document (raw JSON, "" when missing or refused) — editors only, server-enforced */
export async function getProofVersionDoc(id: string, v: number): Promise<string> {
  const r = await apiFetch(u("/proof-version", { id, v, part: "doc" }), { cache: "no-store", headers: proofIdentityHeaders() });
  return r.ok ? r.text() : "";
}
